import type { ComponentConfig } from "@intelligems/ig-types/dtos/components";
import { createAsyncThunk } from "@src/store/createAsyncThunk";
import { ensureChatSessionId } from "@src/store/slices/experienceBuilder/ai/thunks/ensureChatSessionId";
import { getInitialMods } from "@src/store/slices/experienceBuilder/common/selectors/getInitialMods";
import { getInitialVariationId } from "@src/store/slices/experienceBuilder/common/selectors/getInitialVariationId";
import { isOnsiteUpsellBuilder } from "@src/store/slices/experienceBuilder/common/selectors/isOnsiteUpsellBuilder";
import {
  trackBuilderOpened,
  trackOnsiteUpsellEdited,
} from "@src/store/slices/experienceBuilder/common/thunks/mixPanel";
import { updatePluginConfig } from "@src/store/slices/experienceBuilder/common/thunks/updatePluginConfig";
import { readDemoUrl } from "@src/store/slices/experienceBuilder/common/utils/demoUrlStorage";
import { resolveInitialUrl } from "@src/store/slices/experienceBuilder/common/utils/resolveFullUrl";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { openDialog } from "@src/store/slices/global";
import { getOrganization } from "@src/store/slices/organization";
import { getCanCreateBundleOnsiteUpsell } from "@src/store/slices/organization/selectors/getCanCreateBundleOnsiteUpsell";
import { v4 } from "uuid";

const { initBuilder } = experienceBuilderSlice.actions;

export const openBuilder = createAsyncThunk<
  void,
  {
    /** The test group to open on; omitted picks the default. */
    variationId?: string;
    isOnsiteUpsell?: boolean;
    initialComponentType?: ComponentConfig["type"];
  } | void
>("experienceBuilder/openBuilder", (opts, { dispatch, getState }) => {
  const isOnsiteUpsell = opts?.isOnsiteUpsell ?? false;
  // The creation chat can hand off a bundle regardless of plan, so an org
  // without the bundle scope opens a plain upsell.
  const initialComponentType =
    opts?.initialComponentType === "bundleOnsiteUpsell" &&
    !getCanCreateBundleOnsiteUpsell(getState())
      ? undefined
      : opts?.initialComponentType;
  const mods = getInitialMods(getState());
  const variationId = opts?.variationId ?? getInitialVariationId(getState());
  const org = getOrganization(getState());

  // Hydrate the demo URL from its persistence edge (sessionStorage,
  // org-scoped) once, here — the only impure read. Feed it into initBuilder so
  // the reducer sets both `demoUrl` and the initial `targetUrl` from the
  // same value; everything downstream reads it from redux.
  const demoUrl = readDemoUrl(org?.id);
  // Opens at the demo URL as configured (path and all); later navigations
  // resolve their own paths against that store's origin via `resolveFullUrl`.
  const targetUrl = resolveInitialUrl(org, demoUrl);

  // Seed the Viv chat session id up front, before anything tracks: the id is
  // client-generated and the server only materializes the session on the first
  // send, so every event fired inside the builder — including those before the
  // merchant says anything — can be attributed to the chat it may go on to
  // start. Paired with the `clearAiChat` on builder close (see
  // `setupBuilderLifecycleListener`), so each builder session gets its own id.
  void dispatch(ensureChatSessionId());

  void dispatch(trackBuilderOpened());

  if (isOnsiteUpsell) {
    void dispatch(trackOnsiteUpsellEdited());
  }

  dispatch(
    initBuilder({
      mods,
      variationId,
      targetUrl,
      cacheKey: v4(),
      demoUrl,
      isOnsiteUpsell,
      initialComponentType,
    })
  );

  // Keyed off the mods, not `experience.type`, so it's right for personalizations.
  const modificationName = isOnsiteUpsellBuilder(getState())
    ? "Onsite Upsells"
    : "Onsite Edits";

  dispatch(
    openDialog({
      dialog: "experienceBuilder",
      props: { modificationName },
    })
  );

  void dispatch(updatePluginConfig());
});
